import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import {
  KitRequestAllocationStatus,
  getLinePrimaryStatus,
} from 'hooks/useGetDecoratedKitRequestLines/utils';
import { COLOR_OPTIONS } from 'types';
import { RelatedRecord, RelatedRecordType } from 'types/inventory';
import { KitRequestSubtype } from 'views/kitting/Kit/KitRequestContext';

export const filterUnallocatedLines = (
  lines: DecoratedKitRequestLine[]
): DecoratedKitRequestLine[] =>
  lines.filter((line) => line.allocatedQuantity < (line.minimumQuantity || 0));

export const filterAllocatedLines = (
  lines: DecoratedKitRequestLine[]
): DecoratedKitRequestLine[] =>
  lines.filter((line) => line.allocatedQuantity >= (line.minimumQuantity || 0));

export const calculateAllocationStats = (lines: DecoratedKitRequestLine[]) => {
  const fullyAllocated = filterAllocatedLines(lines).length;
  const partialOrUnallocated = filterUnallocatedLines(lines).length;

  return {
    total: lines.length,
    fullyAllocated,
    partialOrUnallocated,
    allocationRate:
      lines.length > 0 ? Math.round((fullyAllocated / lines.length) * 100) : 0,
  };
};

export const calculateAllocationStatusCounts = (
  lines: DecoratedKitRequestLine[]
) => {
  const statusCounts = {
    onHand: 0,
    expected: 0,
    unavailable: 0,
  };

  lines.forEach((line) => {
    const primaryStatus = getLinePrimaryStatus(line.stats);

    switch (primaryStatus) {
      case KitRequestAllocationStatus.OnHand:
      case KitRequestAllocationStatus.ReadyToPack:
      case KitRequestAllocationStatus.Packed:
      case KitRequestAllocationStatus.Shipped:
      case KitRequestAllocationStatus.Delivered:
        statusCounts.onHand += 1;
        break;
      case KitRequestAllocationStatus.Expected:
        statusCounts.expected += 1;
        break;
      case KitRequestAllocationStatus.Unavailable:
      case KitRequestAllocationStatus.Quarantined:
      case KitRequestAllocationStatus.NotInFacility:
      case KitRequestAllocationStatus.Unknown:
      default:
        statusCounts.unavailable += 1;
        break;
    }
  });

  const sections = [];
  if (statusCounts.onHand > 0) {
    sections.push({
      key: 'onHand',
      label: 'On Hand',
      value: statusCounts.onHand,
      color: COLOR_OPTIONS.success,
    });
  }
  if (statusCounts.expected > 0) {
    sections.push({
      key: 'expected',
      label: 'Expected',
      value: statusCounts.expected,
      color: COLOR_OPTIONS.primary,
    });
  }
  if (statusCounts.unavailable > 0) {
    sections.push({
      key: 'unavailable',
      label: 'Unavailable',
      value: statusCounts.unavailable,
      color: COLOR_OPTIONS.error,
    });
  }

  return sections;
};

export const buildKitRequestRelatedRecords = (
  kitRequest?: KitRequestSubtype | null
): RelatedRecord[] => {
  if (!kitRequest) return [];

  const records: RelatedRecord[] = [];

  if (kitRequest.team) {
    records.push({
      name: kitRequest.team.name,
      recordType: RelatedRecordType.TEAM,
      recordId: kitRequest.team.id,
      orgId: null,
    });
  }

  if (kitRequest.sourceLocations) {
    kitRequest.sourceLocations.forEach((location) => {
      records.push({
        name: location.name,
        recordType: RelatedRecordType.STOCK_LOCATION,
        recordId: location.id,
        orgId: null,
      });
    });
  }

  if (kitRequest.productionRun) {
    records.push({
      name:
        kitRequest.productionRun.name ||
        kitRequest.productionRun.referenceNumber,
      recordType: RelatedRecordType.PRODUCTION_RUN,
      recordId: kitRequest.productionRun.id,
      orgId: null,
    });
  }

  return records;
};
